import { Component, ElementRef, OnInit, QueryList, ViewChildren } from '@angular/core';
import { CVLocalizationService } from 'src/app/reusables/services/cv-localization.service';
import { MetricDefinition } from 'src/shared/models/pipeline-config/metric-definition.models';
import { MetricTypes } from 'src/shared/models/pipeline-config/mongo-enums.model';
import PipelineConfigurationModalsService from '../pipeline-configuration-modals.service';
import PipelineConfigurationService from '../pipeline-configuration.service';

@Component({
    selector: 'cv-metric-definitions-landing',
    templateUrl: './metric-definitions-landing.component.html',
    styleUrls: ['./metric-definitions-landing.component.scss']
})
export class MetricDefinitionsLandingComponent implements OnInit {

    /** Indicates if the metric definitions are loading */
    isLoading: boolean = true;
    /** Bound model variable of the search box that filters metrics */
    filterValue: string = "";
    /** All loaded metric definitions */
    allDefinitions: MetricDefinition[];
    /** Subset of allDefinitions that match the current filterValue */
    filteredDefinitions: MetricDefinition[];

    /** Collection of the displayed metric categories in the middle column.  This is used to scroll into view when the category on the left is clicked. */
    @ViewChildren("metricCategory")
    displayedMetricCategories: QueryList<ElementRef<HTMLDivElement>>;

    selectedMetricDefinition: MetricDefinition;
    isBlocked: boolean = false; // indicates an action is in progress, such as copying or deleting a metric.
    isEditing: boolean = false; // Used to toggle between seeing the different definitions and the Edit view.

    constructor(
        private service: PipelineConfigurationService,
        private CVLocalizationService: CVLocalizationService,
        private modalsService: PipelineConfigurationModalsService,
    ) {

    }

    async ngOnInit() {
        this.eagerLoadMetricSelector();
        const initialLoadPromises: Promise<any>[] = [
            this.CVLocalizationService.GetSection("Common"),
            this.CVLocalizationService.GetSection("Pipeline_Configuration"),
            this.service.getPipelineConfigurationPermissions(),
            this.service.getReportingSets(),
            this.loadMetricDefinitions(),
        ];
        await Promise.all(initialLoadPromises);
        this.isLoading = false;
    }

    async loadMetricDefinitions() {
        this.service.clearMetricDefinitionCache();
        const metricDefs = await this.service.getMetricDefinitions();
        this.allDefinitions = metricDefs ?? [];
        this.filteredDefinitions = this.allDefinitions;
        this.applyFilter();
    }

    /**
     * Eager loads the metric selector used when editing Calculated Metrics.
     * This component doesn't use the Metric Selector, but it can take a little bit to load.
     */
    private eagerLoadMetricSelector() {
        this.service.getMetricFormulaMetricSelectorHtml();
    }

    /** Applies the filter to only show matching metrics. Matches on Abbreviation, Metric Name, or MetricID */
    applyFilter() {
        if (this.filterValue) {
            let escapedFilterValue = this.escapeRegExp(this.filterValue);
            let regex = new RegExp(escapedFilterValue, "i");
            this.filteredDefinitions = this.allDefinitions.filter(m => regex.test(m.Abreviation) || regex.test(m.MetricName) || m.MetricID.toString() == this.filterValue);
        } else {
            this.filteredDefinitions = this.allDefinitions;
        }
    }

    /** Clears the filter so all metrics are displayed */
    clearFilter() {
        this.filterValue = "";
        this.applyFilter();
    }

    /** Displays the EditMetricComponent to create a new one */
    createNewDefinition() {
        if (this.isLoading || this.isBlocked)
            return;
        this.selectedMetricDefinition = null;
        this.isBlocked = true;
        this.isEditing = true;
    }

    /** Click event callback on the left side Categories. Scrolls the center list of Metrics to display the clicked-on category at the top of the view. */
    scrollCategoryIntoView(metricCategory: string) {
        let category: ElementRef<HTMLDivElement> = this.displayedMetricCategories.find(divs => divs.nativeElement.dataset["category"] == metricCategory);
        category?.nativeElement?.scrollIntoView({ behavior: "smooth" });
    }

    /** Displays the EditMetricComponent to edit an existing metric */
    editMetricDefinition(selectedDefinition: MetricDefinition) {
        if (!selectedDefinition || this.isBlocked)
            return;
        this.isBlocked = true;

        this.selectedMetricDefinition = selectedDefinition;
        this.isEditing = true;
    }

    /**
     * Callback event when EditMetricComponent has validated and saved (using the service) the new or edited metric.
     * Reloads Metric Definitions and tries to select the newly saved definition.
     */
    async editMetricCommitted(def: MetricDefinition) {
        this.isLoading = true;
        this.isBlocked = false;
        this.isEditing = false;
        this.selectedMetricDefinition = null;

        // For Standard or Snapshot metrics, prompt the user if they want to recalculate the just-saved metric:
        if (def.MetricID && (def.Type == MetricTypes.Standard || def.Type == MetricTypes.RealTime)) {
            this.promptMetricRecalc(def);
        }

        await this.loadMetricDefinitions();
        // Select the just-saved metric def after reloading all the definitions.
        if (def.MetricID > 0) {
            const metricDef = this.allDefinitions.find(md => md.MetricID === def.MetricID);
            this.selectedMetricDefinition = metricDef ?? null; // Ensure null instead of undefined
            this.scrollCategoryIntoView(metricDef.Group);
        } else {
            // Try to find it:
            const similarMetrics = this.allDefinitions.filter(md => md.MetricName == def.MetricName && md.Abreviation == def.Abreviation && md.Group == def.Group);
            if (similarMetrics.length == 1) {
                this.selectedMetricDefinition = similarMetrics[0];
                this.scrollCategoryIntoView(def.Group);
            }
        }
        this.isLoading = false;
    }

    /* Asks the user if they want to recalculate the just-saved Metric Definition */
    private promptMetricRecalc(def: MetricDefinition) {
        window.scrollTo(0, 0); // The page is normally scrolled to the bottom, since that's where the "Save" button resides.
        let title = this.CVLocalizationService.getCachedText("Pipeline_Configuration", "Recalculate_Metric", "Recalculate Metric");
        let body = this.CVLocalizationService.getCachedText("Pipeline_Configuration", "Recalculate_Metric_Prompt", 'Would you like to recalculate the metric "METRIC_ABBREVIATION"?');
        body = body.replace("METRIC_ABBREVIATION", def.Abreviation);
        let dialogPromise = this.service.showYesNoDialog(title, body, "info");
        dialogPromise.then((answer: "yes" | "no") => {
            if (answer == "yes") {
                // Open Recalc Select modal with the metric definition selected.
                this.modalsService.showRecalculationSelectionModal({ metricIds: [def.MetricID] }).result.catch(() => {}); // ignore close/dismiss of modal
            }
        }).catch(()=>{}); // ignore any exceptions
    }

    /** Callback event when the user has clicked Back or Cancel in EditMetricComponent */
    editMetricCanceled() {
        this.isBlocked = false;
        this.isEditing = false;
    }

    /** Callback event when the user clicks on the Copy icon in the right side Properties pane.  Duplicates the selected metric. */
    async copyMetricDefinition(selectedDefinition: MetricDefinition) {
        if (this.isBlocked)
            return;
        this.isBlocked = true;

        try {
            await this.service.copyMetricDefinition(selectedDefinition.MetricID)
            this.loadMetricDefinitions();
            this.isBlocked = false;
        } catch (err) {
            console.error("Failed to copy metric definition:", err);
            window["showErrorDialog"]("Could not copy metric definition.", "Metric Definition Error");
            this.isBlocked = false;
        }
    }

    /** Callback event when the user clicks on the Trash icon in the right side Properties pane.  Deactivates the selected metric. */
    async deactivateMetricDefinition(selectedDefinition: MetricDefinition) {
        if (this.isBlocked || !this.isEditable(selectedDefinition))
            return;
        this.isBlocked = true;
        this.isLoading = true;

        // Check if this metric is used as a component to other Calculated Metrics
        try {
            let reverseDependencies = await this.service.getMetricReverseDependencies(selectedDefinition.MetricID);
            if (reverseDependencies.length) {
                // Disallow deactivation:
                var message = "This metric can not be deactivated. In order to deactivate please either deactivate or remove this metric as a component from the following:<ul>";
                reverseDependencies.forEach(abv => message += "<li>" + htmlEncode(abv) + "</li>");
                message += "</ul>";
                window["showConfirmDialog"](message, [
                    {
                        text: "Ok",
                        click: (e: Event) => {
                            e.stopPropagation();
                            (jQuery(".errorDialog") as any).dialog("close");
                        }
                    }
                ], "Error Deactivating Metric Definition", "Error");
            } else {
                // Prompt the user 
                let result = await this.warn("Warning", "Are you sure you want to deactivate the selected metric definition?");

                if (result === "yes") {
                    try {
                        await this.service.deactivateMetricDefinition(selectedDefinition.MetricID);
                        this.loadMetricDefinitions();
                        this.selectedMetricDefinition = null;
                    } catch (err) {
                        var msg;
                        if (err && err.warnings && err.warnings[0])
                            msg = err.warnings[0];
                        else
                            msg = "Error code: " + (err && err.status_code || 0);
                        window["showErrorDialog"]("Could not deactivate metric definition. " + msg, "Error Deactivate Metric Definition");
                    }
                }
            }
        } finally {
            this.isBlocked = false;
            this.isLoading = false;
        }
    }

    private warn(title: string, message: string): Promise<"yes" | "no"> {
        return new Promise<"yes" | "no">((resolve, reject) => {
            const buttons =
                [
                    {
                        "text": this.CVLocalizationService.getCachedText("Common", "Yes", "Yes"),
                        click: () => {
                            (jQuery(".errorDialog") as any).dialog("close");
                            resolve("yes");
                        },
                    },
                    {
                        "text": this.CVLocalizationService.getCachedText("Common", "No", "No"),
                        click: () => {
                            (jQuery(".errorDialog") as any).dialog("close");
                            resolve("no");
                        }
                    }
                ];
            window["showConfirmDialog"](message, buttons, title, "warning");
        });
    }

    selectMetricDefinition(def: MetricDefinition) {
        this.selectedMetricDefinition = def;
    }

    isEditable(definition: MetricDefinition) {
        return this.service.isEditable(definition);
    }

    // Utility function to replace any special regex characters in a string with their escaped version.
    private escapeRegExp(string) {
        return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    }
}
